import { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import { AppContext } from '@edx/frontend-platform/react';
import {
  Avatar, Badge, Button, DataTable, Dropdown, Skeleton,
} from '@openedx/paragon';
import { RiCalendarLine } from '@remixicon/react';

import messages from '../messages';
import { useLeaderboard, LEADERBOARD_WINDOWS, DEFAULT_PAGE_SIZE } from '../leaderboardHooks';
import { posthogCaptureFeatureUsage } from '@blend-ed/blendxplatform';

import './LeaderboardSection.scss';

const WINDOW_OPTIONS = [
  { key: LEADERBOARD_WINDOWS.WEEK, msg: 'pointsWindowWeek' },
  { key: LEADERBOARD_WINDOWS.MONTH, msg: 'pointsWindowMonth' },
  { key: LEADERBOARD_WINDOWS.ALL, msg: 'pointsWindowAll' },
];

/**
 * Org leaderboard for the learner progress page. Mirrors the staff analytics
 * leaderboard but is read-only and highlights the current learner's row.
 * Visually matches the Progress page cards (StatsCard / ActivityMap).
 * Self-hides when the leaderboard feature is off (caller passes `enabled`).
 */
const LeaderboardSection = ({ enabled = true }) => {
  const intl = useIntl();
  const { authenticatedUser } = useContext(AppContext);
  const currentUserId = authenticatedUser?.userId || authenticatedUser?.id || null;

  const [timeWindow, setTimeWindow] = useState(LEADERBOARD_WINDOWS.WEEK);
  const [page, setPage] = useState(1);
  const { data, isLoading } = useLeaderboard(timeWindow, page, DEFAULT_PAGE_SIZE, enabled);

  const rows = data?.leaderboard ?? [];
  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.ceil(total / DEFAULT_PAGE_SIZE);

  const fetchData = useCallback(({ pageIndex }) => {
    setPage(pageIndex + 1);
  }, []);

  const handleWindowChange = (key) => {
    setTimeWindow(key);
    setPage(1);
  };

  useEffect(() => {
    if (enabled) {
      posthogCaptureFeatureUsage('leaderboard', { action: 'viewed', surface: 'learner' });
    }
  }, [enabled]);

  if (!enabled) {
    return null;
  }

  const activeOption = WINDOW_OPTIONS.find((o) => o.key === timeWindow);

  /* eslint-disable react/prop-types, react/no-unstable-nested-components */
  const LearnerCell = ({ row }) => {
    const { full_name: fullName, avatar_url: avatarUrl, user_id: userId } = row.original;
    const isSelf = currentUserId != null && String(userId) === String(currentUserId);
    return (
      <div className="leaderboard-section__learner">
        <Avatar size="sm" src={avatarUrl || undefined} alt="" />
        <span className="leaderboard-section__learner-name">{fullName}</span>
        {isSelf && (
          <Badge variant="primary" className="leaderboard-section__you-badge">
            {intl.formatMessage(messages.leaderboardYou)}
          </Badge>
        )}
      </div>
    );
  };
  /* eslint-enable react/prop-types, react/no-unstable-nested-components */

  return (
    <>
      <div className="leaderboard-section__section-header">
        <h2 className="leaderboard-section__section-title">
          {intl.formatMessage(messages.leaderboardTitle)}
        </h2>
        <Dropdown>
          <Dropdown.Toggle
            id="learner-leaderboard-window"
            as={Button}
            variant="ghost-secondary"
            size="sm"
            iconBefore={RiCalendarLine}
          >
            {intl.formatMessage(messages[activeOption.msg])}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {WINDOW_OPTIONS.map((option) => (
              <Dropdown.Item
                key={option.key}
                active={option.key === timeWindow}
                onClick={() => handleWindowChange(option.key)}
              >
                {intl.formatMessage(messages[option.msg])}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="leaderboard-section__card">
        {isLoading && rows.length === 0 ? (
          <Skeleton height={300} />
        ) : (
          <DataTable
            key={timeWindow}
            isLoading={isLoading}
            isPaginated
            manualPagination
            fetchData={fetchData}
            data={rows}
            itemCount={total}
            pageCount={pageCount}
            initialState={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
            columns={[
              {
                Header: intl.formatMessage(messages.leaderboardColumnRank),
                accessor: 'rank',
              },
              {
                Header: intl.formatMessage(messages.leaderboardColumnLearner),
                accessor: 'full_name',
                Cell: LearnerCell,
              },
              {
                Header: intl.formatMessage(messages.leaderboardColumnTotalPoints),
                accessor: 'total_points',
              },
            ]}
          >
            <DataTable.Table />
            <DataTable.EmptyTable content={intl.formatMessage(messages.leaderboardEmpty)} />
            {pageCount > 1 && <DataTable.TableFooter />}
          </DataTable>
        )}
      </div>
    </>
  );
};

LeaderboardSection.propTypes = {
  enabled: PropTypes.bool,
};

LeaderboardSection.defaultProps = {
  enabled: true,
};

export default LeaderboardSection;
